import { VALIDATION_CHECK_CATALOG, normalizeValidationErrors } from '../constants/validationCheckCatalog';
import { hasAllRequiredM11Sections } from './m11Validation';
import { normalizeProtocolSectionsList } from './protocolExportContent';
import administrativeInfoService from '../services/administrativeInfo.service';
import { resolveAdminSponsorFields } from './administrativeSponsorFields';
import clinicalIntakeService from '../services/clinicalIntake.service';
import { projectService } from '../services/projectService';
import {
  resolveProjectSponsorName,
  resolveSponsorNameForDisplay,
  resolveSponsorProtocolIdForDisplay,
} from './projectIdentityFields';
import { pickHeaderProtocolNumber } from './protocolExportHeaderFooter';
import {
  validateFontComplianceForExport,
  validateOptionalSectionsForExport,
  validateSubsectionHierarchyForExport,
  validateTableNumberingForExport,
  validateObjectiveHierarchyForExport,
  validateSoALandscapeForExport,
  validateSIUnitsConsistencyForExport,
} from './m11Validation';

export const validateM11SectionsForExport = (sections = []) => {
  const normalizedSections = normalizeProtocolSectionsList(sections);
  const { isValid, missing } = hasAllRequiredM11Sections(normalizedSections);

  if (isValid) {
    return {
      isValid: true,
      missing: [],
      errors: [],
    };
  }

  const missingList = Array.isArray(missing) ? missing : [];
  const errorMessage = missingList.length
    ? `Missing required M11 sections: ${missingList.join(', ')}`
    : 'Missing required M11 sections.';

  return {
    isValid: false,
    missing: missingList,
    errors: [errorMessage],
  };
};

/**
 * Validate consistency between title page and header data
 * Checks that protocol number, version number, version date, and sponsor information
 * are consistent between the title page (from administrative info) and headers
 */
export const validateTitlePageHeaderConsistency = async (projectId, titlePageConfig = null) => {
  const errors = [];
  
  if (!projectId) {
    return { isValid: true, errors: [] }; // Skip validation if no project ID
  }
  
  try {
    // Fetch administrative info for title page data
    let titlePageProtocolNumber = '';
    let titlePageVersionNumber = '';
    let titlePageVersionDate = '';
    let titlePageSponsorName = '';
    let titlePageCoSponsor = '';
    let titlePageLocalSponsor = '';
    
    // Same sources the Word/PDF export reads: this project's Admin Info, the
    // project record (canonical Sponsor Protocol Identifier) and clinical intake.
    let adminInfo = null;
    let projectData = null;
    let clinicalIntakeData = null;
    try {
      const adminResponse = await administrativeInfoService.getAllAdministrativeInfo(projectId);
      adminInfo = adminResponse?.data?.[0] || null;
    } catch (adminErr) {
      console.warn('Could not fetch administrative info for validation:', adminErr);
    }
    try {
      const projectRes = await projectService.getProject(projectId);
      projectData = projectRes?.data ?? projectRes;
    } catch (projectErr) {
      console.warn('Could not fetch project for validation:', projectErr);
    }
    try {
      clinicalIntakeData = await clinicalIntakeService.getLatestClinicalIntake(projectId);
    } catch (clinicalErr) {
      console.warn('Could not fetch clinical intake for validation:', clinicalErr);
    }

    // Title page values (same precedence as the Title Page tab: project first, then Admin Info)
    titlePageProtocolNumber =
      titlePageConfig?.sponsorProtocolIdentifier?.text ||
      resolveSponsorProtocolIdForDisplay(projectData, adminInfo);
    if (adminInfo) {
      titlePageVersionNumber = (adminInfo.version_number || adminInfo.versionNumber)?.toString() || '';
      if (adminInfo.version_date) {
        titlePageVersionDate = new Date(adminInfo.version_date).toLocaleDateString();
      }
      const sponsorFields = resolveAdminSponsorFields(adminInfo);
      titlePageCoSponsor = sponsorFields.coSponsorName;
      titlePageLocalSponsor = sponsorFields.localSponsorName;
    }
    titlePageSponsorName = resolveSponsorNameForDisplay(projectData, adminInfo);

    // Header values (same logic as exportToWord / resolveProtocolHeaderFooterInfo)
    const headerProtocolNumber = pickHeaderProtocolNumber({
      clinicalIntake: clinicalIntakeData,
      titlePageConfig,
      project: projectData,
      adminInfo,
    });
    let headerVersionNumber = '';
    let headerVersionDate = '';
    let headerSponsorName = '';
    if (adminInfo) {
      headerVersionNumber = (adminInfo.version_number || adminInfo.versionNumber)?.toString() || '';
      if (adminInfo.version_date) {
        headerVersionDate = new Date(adminInfo.version_date).toLocaleDateString();
      }
      headerSponsorName = String(
        resolveProjectSponsorName(projectData) || resolveAdminSponsorFields(adminInfo).sponsorName || '',
      ).trim();
    }

    if (!headerProtocolNumber) {
      errors.push(`Protocol number missing in header: no protocol number found in clinical intake, the study's Sponsor Protocol Identifier or Administrative Info.`);
    }
    
    // Compare version number
    if (titlePageVersionNumber && headerVersionNumber) {
      if (titlePageVersionNumber.trim() !== headerVersionNumber.trim()) {
        errors.push(`Version number mismatch: Title page shows "${titlePageVersionNumber}" but header will show "Version ${headerVersionNumber}". They must match.`);
      }
    } else if (titlePageVersionNumber && !headerVersionNumber) {
      errors.push(`Version number missing in header: Title page has "${titlePageVersionNumber}" but header data is missing.`);
    } else if (!titlePageVersionNumber && headerVersionNumber) {
      errors.push(`Version number missing in title page: Header has "${headerVersionNumber}" but title page data is missing.`);
    }
    
    // Compare version date (if both exist)
    if (titlePageVersionDate && headerVersionDate) {
      // Normalize dates for comparison (handle different date formats)
      const normalizeDate = (dateStr) => {
        try {
          const date = new Date(dateStr);
          return date.toISOString().split('T')[0]; // YYYY-MM-DD format
        } catch {
          return dateStr.trim();
        }
      };
      const normalizedTitleDate = normalizeDate(titlePageVersionDate);
      const normalizedHeaderDate = normalizeDate(headerVersionDate);
      if (normalizedTitleDate !== normalizedHeaderDate) {
        errors.push(`Version date mismatch: Title page shows "${titlePageVersionDate}" but header data shows "${headerVersionDate}". They must match.`);
      }
    }
    
    // Check sponsor & collaborators name consistency in title page
    // (Header doesn't display sponsor name, but we validate title page has it)
    // Validate that sponsor name is present in title page
    // Note: titlePageSponsorName is already just the name (not combined with address)
    if (!titlePageSponsorName && !titlePageCoSponsor && !titlePageLocalSponsor) {
      errors.push(`Sponsor & Collaborators name missing: At least one sponsor (Sponsor, Co-Sponsor, or Local Sponsor) must be specified in the title page.`);
    }
    
    // Compare sponsor name (both are now just the name, not combined with address)
    if (headerSponsorName && titlePageSponsorName) {
      const normalizedTitleSponsor = titlePageSponsorName.trim().toLowerCase();
      const normalizedHeaderSponsor = headerSponsorName.trim().toLowerCase();
      if (normalizedTitleSponsor !== normalizedHeaderSponsor) {
        errors.push(`Sponsor name mismatch: Title page shows "${titlePageSponsorName}" but administrative info shows "${headerSponsorName}". They must match.`);
      }
    }
    
    console.log('Title page/header consistency check:', {
      titlePage: {
        protocolNumber: titlePageProtocolNumber,
        versionNumber: titlePageVersionNumber,
        versionDate: titlePageVersionDate,
        sponsorName: titlePageSponsorName
      },
      header: {
        protocolNumber: headerProtocolNumber,
        versionNumber: headerVersionNumber,
        versionDate: headerVersionDate,
        sponsorName: headerSponsorName
      },
      errors
    });
    
    return { isValid: errors.length === 0, errors };
  } catch (err) {
    console.error('Error validating title page/header consistency:', err);
    return { 
      isValid: false, 
      errors: [`Failed to validate title page/header consistency: ${err.message}`] 
    };
  }
};

/**
 * Run all export validation checks and return results
 * This is a reusable function that can be called from both export and validation button
 * @param {Array} sections - Array of document sections
 * @param {string} projectId - Project ID for title page/header validation
 * @param {Object} generatedSoAData - Resolved Schedule of Activities data, as passed to the export (optional)
 * @param {Object} titlePageConfig - Title page config used by the export (optional)
 * @returns {Promise<Array>} Array of validation results with { id, label, passed, errors }
 */
export const runAllExportValidations = async (
  sections = [],
  projectId = null,
  generatedSoAData = null,
  titlePageConfig = null,
) => {
  const validationResults = [];

  const addValidationResult = (config) => {
    const { id, label, passed, errors, defaultMessage, methodology = 'rule' } = config;
    const list = normalizeValidationErrors(errors);
    const messages = list.length > 0 ? list : passed ? [] : [defaultMessage];
    const meta = VALIDATION_CHECK_CATALOG[id] || {};
    validationResults.push({
      id,
      label,
      passed,
      errors: messages,
      methodology,
      howChecked: meta.howChecked,
      passCriteria: meta.passCriteria,
    });
  };

  // 1. M11 Sections Validation
  const m11Result = validateM11SectionsForExport(sections);
  if (!m11Result.isValid) {
    console.warn('M11 validation failed:', {
      errors: m11Result.errors,
      missingSections: m11Result.missing,
    });
  }
  addValidationResult({
    id: 'm11',
    label: 'Required ICH M11 Sections',
    passed: m11Result.isValid,
    errors: m11Result.errors,
    defaultMessage: 'Export blocked. Required M11 sections are missing.',
  });

  // 2. Font Compliance Validation
  const fontResult = validateFontComplianceForExport(sections);
  if (!fontResult.isValid) {
    console.warn('Font validation failed:', fontResult.errors);
  }
  addValidationResult({
    id: 'font',
    label: 'Font Compliance (Times New Roman, Draft 1.0 §4.1 sizes)',
    passed: fontResult.isValid,
    errors: fontResult.errors,
    defaultMessage:
      'Export blocked. Document must use Times New Roman with sizes allowed by Draft 1.0 §4.1 (e.g. body 12pt, main headings 14pt, subordinate headings/tables 11pt, footnotes/headers 10pt, title 18pt, min table 9pt).',
  });

  // 3. Optional Sections Validation
  const optionalResult = validateOptionalSectionsForExport(sections);
  if (!optionalResult.isValid) {
    console.warn('Optional section validation failed:', optionalResult.errors);
  }
  addValidationResult({
    id: 'optional-sections',
    label: 'Optional Sections Marked as Not Provided',
    passed: optionalResult.isValid,
    errors: optionalResult.errors,
    defaultMessage: 'Export blocked. Optional sections without content must explicitly mark the title as "(Not Provided)".',
  });

  // 4. Subsection Hierarchy Validation
  const hierarchyResult = validateSubsectionHierarchyForExport(sections);
  if (!hierarchyResult.isValid) {
    console.warn('Subsection hierarchy validation failed:', hierarchyResult.errors);
  }
  addValidationResult({
    id: 'subsection-hierarchy',
    label: 'Section & Subsection Sequencing',
    passed: hierarchyResult.isValid,
    errors: hierarchyResult.errors,
    defaultMessage: 'Export blocked. Subsection numbering must be sequential (e.g., 1.1, 1.2, 1.3).',
  });

  // 5. Table Numbering Validation
  const tableNumberResult = validateTableNumberingForExport(sections);
  if (!tableNumberResult.isValid) {
    console.warn('Table numbering validation failed:', tableNumberResult.errors);
  }
  addValidationResult({
    id: 'table-numbering',
    label: 'Global Table Numbering Sequence',
    passed: tableNumberResult.isValid,
    errors: tableNumberResult.errors,
    defaultMessage: 'Export blocked. Tables must be numbered sequentially across the document (Table 1, Table 2, Table 3, ...).',
  });

  // 6. Objective Hierarchy Validation
  const objectivesResult = validateObjectiveHierarchyForExport(sections);
  if (!objectivesResult.isValid) {
    console.warn('Objective hierarchy validation failed:', objectivesResult.errors);
  }
  addValidationResult({
    id: 'objective-hierarchy',
    label: 'Objective Hierarchy (Primary → Secondary → Exploratory)',
    passed: objectivesResult.isValid,
    errors: objectivesResult.errors,
    defaultMessage: 'Export blocked. Objective sections must appear in Primary, Secondary, Exploratory order.',
  });

  // 7. SOA Landscape Validation
  const soaLandscapeResult = validateSoALandscapeForExport(sections, generatedSoAData);
  if (!soaLandscapeResult.isValid) {
    console.warn('SOA landscape validation failed:', soaLandscapeResult.errors);
  }
  addValidationResult({
    id: 'soa-landscape',
    label: 'Schedule of Activities Landscape Layout',
    passed: soaLandscapeResult.isValid,
    errors: soaLandscapeResult.errors,
    defaultMessage: 'Export blocked. Schedule of Activities must be generated and configured for landscape layout.',
  });

  // 8. SI Units Consistency Validation
  const siUnitsResult = validateSIUnitsConsistencyForExport(sections);
  if (!siUnitsResult.isValid) {
    console.warn('SI units consistency validation failed:', siUnitsResult.errors);
  }
  addValidationResult({
    id: 'si-units-consistency',
    label: 'SI Units & Symbols Consistency',
    passed: siUnitsResult.isValid,
    errors: siUnitsResult.errors,
    defaultMessage: 'Export blocked. Units must use consistent SI symbols (kg, g, mg, mL, L, m, cm, mm, etc.) across text and tables.',
  });

  // 9. Title Page & Header Consistency Validation (async)
  const consistencyResult = await validateTitlePageHeaderConsistency(projectId, titlePageConfig);
  if (!consistencyResult.isValid) {
    console.warn('Title page/header consistency validation failed:', consistencyResult.errors);
  }
  addValidationResult({
    id: 'title-page-header-consistency',
    label: 'Title Page & Header Data Consistency',
    passed: consistencyResult.isValid,
    errors: consistencyResult.errors,
    defaultMessage: 'Export blocked. Protocol number, version number, version date, and sponsor information must be consistent between title page and headers.',
  });

  return validationResults;
};

export default {
  validateM11SectionsForExport,
  validateTitlePageHeaderConsistency,
  runAllExportValidations,
};












